import { usePracticeInvite } from '@/composition/hosts/growth/practice-invite-team-members/usePracticeInvite';
import { toast } from '@heidi/design-system/toast';
import { useCallback, useState } from 'react';

export type EmailErrorType = 'invalid' | 'duplicate';

export const getEmailErrorTranslationKey = (
  errorType: EmailErrorType | null
): 'practiceInviteTeam.duplicateEmail' | 'practiceInviteTeam.invalidEmail' =>
  errorType === 'duplicate'
    ? 'practiceInviteTeam.duplicateEmail'
    : 'practiceInviteTeam.invalidEmail';

interface UseEmailInviteFormOptions {
  onSuccess?: () => void;
  onClose?: () => void;
  successToastTitle?: string;
  maxEmails?: number;
}

export function useEmailInviteForm({
  onSuccess,
  onClose,
  successToastTitle = 'Invites sent',
  maxEmails,
}: UseEmailInviteFormOptions = {}) {
  const [emails, setEmails] = useState<string[]>(['']);
  const [emailErrors, setEmailErrors] = useState<Record<number, EmailErrorType | null>>(
    {}
  );
  const [invitationStatus, setInvitationStatus] = useState<{
    successCount: number;
    failureCount: number;
  } | null>(null);

  const { sendInvites, isPending, isValidEmail } = usePracticeInvite();

  const canAddMoreEmails = maxEmails === undefined ? true : emails.length < maxEmails;

  const resetForm = useCallback(() => {
    setEmails(['']);
    setEmailErrors({});
    setInvitationStatus(null);
  }, []);

  const findDuplicateEmails = useCallback((emailList: string[]): Set<number> => {
    const duplicateIndices = new Set<number>();
    const seenEmails = new Map<string, number>();

    emailList.forEach((email, index) => {
      const trimmed = email.trim().toLowerCase();
      if (trimmed) {
        const existingIndex = seenEmails.get(trimmed);
        if (existingIndex !== undefined) {
          duplicateIndices.add(existingIndex);
          duplicateIndices.add(index);
        } else {
          seenEmails.set(trimmed, index);
        }
      }
    });

    return duplicateIndices;
  }, []);

  const handleAddEmail = useCallback(() => {
    setEmails((prev) => {
      if (maxEmails !== undefined && prev.length >= maxEmails) {
        return prev;
      }
      return [...prev, ''];
    });
  }, [maxEmails]);

  const handleRemoveEmail = useCallback(
    (index: number) => {
      setEmails((prev) => {
        if (prev.length <= 1) return prev;
        const newEmails = prev.filter((_, i) => i !== index);
        // Re-validate duplicates after removal
        const duplicates = findDuplicateEmails(newEmails);
        setEmailErrors((currentErrors) => {
          const updated = { ...currentErrors };
          delete updated[index];
          const reindexed: Record<number, EmailErrorType | null> = {};
          Object.entries(updated).forEach(([key, value]) => {
            const keyNum = Number(key);
            const newIndex = keyNum > index ? keyNum - 1 : keyNum;
            // If it was a duplicate error, check if it's still a duplicate
            if (value === 'duplicate') {
              reindexed[newIndex] = duplicates.has(newIndex) ? 'duplicate' : null;
            } else {
              reindexed[newIndex] = value;
            }
          });
          // Remove null entries
          Object.keys(reindexed).forEach((key) => {
            if (reindexed[Number(key)] === null) {
              delete reindexed[Number(key)];
            }
          });
          return reindexed;
        });
        return newEmails;
      });
    },
    [findDuplicateEmails]
  );

  const handleEmailChange = useCallback((index: number, value: string) => {
    setEmails((prev) => {
      const newEmails = [...prev];
      newEmails[index] = value;
      return newEmails;
    });
    // Clear error for the changed field when user starts typing
    setEmailErrors((prev) => {
      if (prev[index]) {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      }
      return prev;
    });
  }, []);

  const handleSendInvites = useCallback(async () => {
    const errors: Record<number, EmailErrorType> = {};
    const validEmails: string[] = [];

    // First pass: check for invalid emails
    emails.forEach((email, index) => {
      const trimmed = email.trim().toLowerCase();
      if (trimmed) {
        if (!isValidEmail(trimmed)) {
          errors[index] = 'invalid';
        }
      }
    });

    // Second pass: check for duplicates (only among valid emails)
    const duplicates = findDuplicateEmails(emails);
    duplicates.forEach((index) => {
      // Only mark as duplicate if not already marked as invalid
      if (!errors[index]) {
        errors[index] = 'duplicate';
      }
    });

    setEmailErrors(errors);

    if (Object.keys(errors).length > 0) return { sent: false };

    // Collect valid, non-duplicate emails
    emails.forEach((email) => {
      const trimmed = email.trim().toLowerCase();
      if (trimmed && isValidEmail(trimmed)) {
        validEmails.push(trimmed);
      }
    });

    if (validEmails.length === 0) {
      const firstEmail = emails[0];
      if (emails.length > 0 && firstEmail !== undefined && !firstEmail.trim()) {
        setEmailErrors({ 0: 'invalid' });
      }
      return { sent: false };
    }

    const result = await sendInvites(validEmails);
    if (result) {
      const { successCount, failedEmails } = result;
      if (successCount > 0) {
        toast({ title: successToastTitle, tone: 'success' });
        onSuccess?.();
      }
      if (failedEmails.length > 0) {
        setInvitationStatus({ successCount, failureCount: failedEmails.length });
        setEmails(failedEmails);
        return { sent: true, hasFailures: true };
      } else {
        onClose?.();
        return { sent: true, hasFailures: false };
      }
    }
    return { sent: false };
  }, [
    emails,
    isValidEmail,
    findDuplicateEmails,
    sendInvites,
    onClose,
    onSuccess,
    successToastTitle,
  ]);

  return {
    emails,
    emailErrors,
    invitationStatus,
    isPending,
    canAddMoreEmails,
    resetForm,
    handleAddEmail,
    handleRemoveEmail,
    handleEmailChange,
    handleSendInvites,
  };
}
